import { useState } from 'react';
import { httpClient } from '../api/http';
import { Alert } from '@piggytechng/component-library';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { setModalActions } from '../store/slices/utils';
import { authPayload } from '../helpers/utils/auth-payload';
import { PiggybankWithdrawalDays, SetWithdrawalDayPayload } from '../types';
import useRolePermissions from './useRolePermissions';

const usePiggybank = () => {
  const { restrictActionByRole, isSuperAdmin } = useRolePermissions({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRunningWithdrawalDayAction, setIsRunningWithdrawalDayAction] = useState<boolean>(false);
  const [isRunningAutosaveAction, setIsRunningAutosaveAction] = useState<boolean>(false);
  const [piggybankBalance, setPiggybankBalance] = useState();
  const [piggybankTransactions, setPiggybankTransactions] = useState([]);
  const [piggybankTransaction, setPiggybankTransaction] = useState();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const params = useParams();
  const userIdIn = params.id as string;

  const [transactionsParams, setTransactionsParams] = useState({
    page: 1,
    limit: 30,
  });

  const [autosaveToggleHistoryParams, setAutosaveToggleHistoryParams] = useState({
    page: 1,
    limit: 20,
  });

  const [isSearchingPiggybank, setIsSearchingPiggybank] = useState<boolean>(false);
  const [searchedPiggybankTransactions, setSearchedPiggybankTransactions] = useState<any[] | null>(null);

  const {
    data: piggybankBalanceQ,
    // isLoading: isLoadingPiggybankBalanceQ,
    // refetch: refetchPiggybankBalanceQ,
  } = useQuery({
    queryKey: ['piggybank-balance' + userIdIn],
    queryFn: () => httpClient.get(`admin/piggybank/fetch-balance/${userIdIn}`).then((res) => res.data.data),
  });

  const {
    data: piggybankTransactionsQ,
    isLoading: isLoadingPiggybankTransactionsQ,
    refetch: refetchPiggybankTransactionsQ,
  } = useQuery({
    queryKey: ['piggybank-transactions' + userIdIn + transactionsParams.page + transactionsParams.limit],
    queryFn: () =>
      httpClient
        .get(`admin/piggybank/transactions/${userIdIn}/${transactionsParams.page}/${transactionsParams.limit}`)
        .then((res) => res.data.data),
  });

  const { data: piggybankWalletDataQ } = useQuery({
    queryKey: ['piggybank-wallet-data' + userIdIn],
    enabled: !!userIdIn,
    queryFn: () => httpClient.get(`admin/piggybank/wallet-data/${userIdIn}`).then((res) => res.data?.data),
  });

  const {
    data: autosaveToggleHistoryData,
    isLoading: isLoadingAutosaveToggleHistoryQ,
    refetch: refetchAutosaveToggleHistoryQ,
  } = useQuery({
    queryKey: [
      'autosave-toggle-history' + userIdIn + autosaveToggleHistoryParams.page + autosaveToggleHistoryParams.limit,
    ],
    enabled: !!userIdIn,
    queryFn: () =>
      httpClient
        .get(
          `admin/user/autosave-toggle-history/${userIdIn}/${autosaveToggleHistoryParams.page}/${autosaveToggleHistoryParams.limit}`,
        )
        .then((res) => res.data.data),
  });

  const autosaveToggleHistoryQ = autosaveToggleHistoryData?.toggleHistory;
  const autosaveToggleHistoryPagination = autosaveToggleHistoryData?.pagination;

  const fetchPiggybankTransactions = async (userId: string, page: number, limit: number) => {
    try {
      setIsLoading(true);
      const url = `admin/piggybank/transactions/${userId}/${page}/${limit}`;
      const response = await httpClient.get(url);
      if (response.data.status) {
        setPiggybankTransactions(response.data.results);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPiggybankTransaction = async (reference: string) => {
    try {
      setIsLoading(true);
      const url = `admin/piggybank/transaction/${reference}`;
      const response = await httpClient.get(url);
      if (response.data.status) {
        setPiggybankTransaction(response.data.transaction);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const searchPiggybankTransactions = async (query: string, filter = 'reference', page = 1, limit = 30) => {
    if (!query.trim()) {
      Alert.error('Please enter a search value');
      return;
    }
    try {
      setIsSearchingPiggybank(true);
      const url = `admin/piggybank/transactions/search/${userIdIn}/${filter}/${page}/${limit}`;
      const response: any = await httpClient.post(url, { query: query.trim() });
      if (response.data.status) {
        const data = response.data.data;
        const results = data?.transactions ?? data?.results ?? data?.transaction ?? (Array.isArray(data) ? data : []);
        setSearchedPiggybankTransactions(results ?? []);
      }
    } catch (error: any) {
      Alert.error(error?.response?.data?.message || 'Failed to search transactions');
    } finally {
      setIsSearchingPiggybank(false);
    }
  };

  const clearPiggybankSearch = () => {
    setSearchedPiggybankTransactions(null);
  };

  const updateTransactionMemo = async (reference: string, memo: string) => {
    try {
      setIsLoading(true);
      const url = `admin/piggybank/transaction/${reference}`;
      const response: any = await httpClient.post(url, { memo });
      if (response.data.status) {
        Alert.success(response.data.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fixAccountBalance = async (fn?: () => void) => {
    try {
      setIsLoading(true);
      const url = `admin/piggybank/fix-account-balance/${userIdIn}`;
      const response: any = await httpClient.get(url);
      if (response.data.status) {
        const balance = response.data.balance;
        fn && fn();
        dispatch(setModalActions({}));
        Alert.success(response.data.message);
        setPiggybankBalance(balance);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTransaction = async (reference: string) => {
    try {
      setIsLoading(true);
      const url = `admin/piggybank/transaction/delete/${reference}`;
      const response: any = await httpClient.delete(url);
      if (response.data.status) {
        Alert.success(response.data.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAutosave = async (userId: string, status: boolean) => {
    try {
      setIsLoading(true);
      const url = `admin/piggybank/toggle-autosave/${userId}/${status}`;
      const response: any = await httpClient.post(url);
      if (response.data.status) {
        Alert.success(response.data.message || `Autosave ${status ? 'enabled' : 'disabled'} successfully`);
        queryClient.invalidateQueries({ queryKey: ['piggybank-wallet-data' + userId] });
        refetchAutosaveToggleHistoryQ();
      }
    } catch (error: any) {
      console.log(error?.response?.data?.message || 'Failed to toggle autosave');
    } finally {
      setIsLoading(false);
    }
  };

  const setupAutosave = async (
    userId: string,
    payload: { amount: number; frequency: string },
    password: string,
    callback?: () => void,
  ) => {
    if (restrictActionByRole('flagAndActions', 'setAutosave')) return;
    try {
      setIsRunningAutosaveAction(true);
      const response = await httpClient.post<any>(`admin/piggybank/setup-autosave/${userId}`, {
        amount: payload.amount,
        frequency: payload.frequency,
        ...authPayload(password),
      });
      if (response.data.status) {
        Alert.success(response.data.message || 'Autosave set successfully');
        queryClient.invalidateQueries({ queryKey: ['piggybank-wallet-data' + userId] });
        callback?.();
      } else {
        Alert.error(response.data.message || 'Autosave was not set');
      }
      return response.data;
    } catch (err: any) {
      console.log(err?.response?.data?.message || 'An error occured trying to set autosave');
    } finally {
      setIsRunningAutosaveAction(false);
    }
  };

  // Pin the customer's free withdrawal day to a custom month/day (core: admin/piggybank/set-withdrawal-day).
  // `force` overrides the 90-day rule; core prefixes the memo with "[admin override] " when it is used.
  const setWithdrawalDay = async (
    userId: string,
    payload: SetWithdrawalDayPayload,
    password: string,
    callback?: (withdrawalDays?: PiggybankWithdrawalDays) => void,
  ) => {
    if (!isSuperAdmin) {
      Alert.warn('You do not have access to perform this action.');
      return;
    }
    try {
      setIsRunningWithdrawalDayAction(true);
      const response = await httpClient.post<any>(`admin/piggybank/set-withdrawal-day/${userId}`, {
        month: payload.month,
        day: payload.day,
        memo: payload.memo,
        force: !!payload.force,
        ...authPayload(password),
      });
      if (response.data.status) {
        Alert.success(response.data.message || 'Withdrawal day set successfully');
        queryClient.invalidateQueries({ queryKey: ['piggybank-wallet-data' + userId] });
        callback?.(response.data.data);
      } else {
        // 90-day rule block: core answers status false with the earliest allowed date
        Alert.error(response.data.message || 'Withdrawal day was not set');
      }
      return response.data;
    } catch (err: any) {
      console.log(err?.response?.data?.message || 'An error occured trying to set the withdrawal day');
    } finally {
      setIsRunningWithdrawalDayAction(false);
    }
  };

  // Drop the custom day and put the customer back on the four PiggyVest days
  const resetWithdrawalDay = async (
    userId: string,
    memo: string,
    password: string,
    callback?: (withdrawalDays?: PiggybankWithdrawalDays) => void,
  ) => {
    if (restrictActionByRole('flagAndActions', 'withdrawalDay')) return;
    if (!isSuperAdmin) {
      Alert.warn('You do not have access to perform this action.');
      return;
    }
    try {
      setIsRunningWithdrawalDayAction(true);
      const response = await httpClient.post<any>(`admin/piggybank/reset-withdrawal-day/${userId}`, {
        memo,
        ...authPayload(password),
      });
      if (response.data.status) {
        Alert.success(response.data.message || 'Customer is back on PiggyVest withdrawal days');
        queryClient.invalidateQueries({ queryKey: ['piggybank-wallet-data' + userId] });
        callback?.(response.data.data);
      } else {
        Alert.error(response.data.message || 'Withdrawal day was not reset');
      }
      return response.data;
    } catch (err: any) {
      console.log(err?.response?.data?.message || 'An error occured trying to reset the withdrawal day');
    } finally {
      setIsRunningWithdrawalDayAction(false);
    }
  };

  return {
    setWithdrawalDay,
    resetWithdrawalDay,
    isRunningWithdrawalDayAction,
    setupAutosave,
    isRunningAutosaveAction,
    isLoading,
    piggybankBalance,
    fetchPiggybankTransactions,
    piggybankTransactions,
    fetchPiggybankTransaction,
    piggybankTransaction,
    updateTransactionMemo,
    fixAccountBalance,
    deleteTransaction,
    piggybankBalanceQ,
    piggybankTransactionsQ,
    isLoadingPiggybankTransactionsQ,
    refetchPiggybankTransactionsQ,
    piggybankWalletDataQ,
    transactionsParams,
    setTransactionsParams,
    toggleAutosave,
    autosaveToggleHistoryQ,
    autosaveToggleHistoryPagination,
    isLoadingAutosaveToggleHistoryQ,
    refetchAutosaveToggleHistoryQ,
    autosaveToggleHistoryParams,
    setAutosaveToggleHistoryParams,
    searchPiggybankTransactions,
    clearPiggybankSearch,
    searchedPiggybankTransactions,
    isSearchingPiggybank,
  };
};

export default usePiggybank;
